import { isRouteErrorResponse, useRouteError } from '@remix-run/react';

import ImageError from '~/assets/error.svg';
import Error401 from '~/assets/error-401.svg';
import Error403 from '~/assets/error-403.svg';
import Error404 from '~/assets/error-404.svg';
import ServerError from '~/assets/error-500.svg';

import { PrimaryNavLinkButton } from './primary-nav-link-button';

export const GeneralErrorBoundary = () => {
  const error = useRouteError();

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex max-w-[425px] flex-col items-center">
        {/* @ts-expect-error TODO write something meaning ful here about why this error is being ignored */}
        <ErrorImage status={error.status} />
        {/* @ts-expect-error TODO write something meaning ful here about why this error is being ignored */}
        <ErrorContent status={error.status} content={error.data?.errors?.[0] || error.data?.error} />
        <DebugLogs error={error} />
      </div>
    </div>
  );
};

interface ErrorImageType {
  status: number;
}

const ErrorImage = ({ status }: ErrorImageType) => {
  let src = null;

  switch (status) {
    case 401: {
      src = Error401;
      break;
    }
    case 403: {
      src = Error403;
      break;
    }
    case 404: {
      src = Error404;
      break;
    }
    default: {
      src = ImageError;
    }
  }

  if (status >= 500) {
    src = ServerError;
  }

  return <img src={src} alt="Insomnia server error logo" />;
};

interface ContentType {
  status: number;
  content?: string;
}

const ErrorContent = ({ status, content }: ContentType) => {
  let title = '';
  let desc = '';

  switch (status) {
    case 404: {
      title = 'Page Not Found';
      desc = `We couldn't find the page you were looking for. Maybe it was never here in the first place.`;
      break;
    }
    default: {
      title = content || 'Something went wrong';
      desc = 'We are already working on fixing it!';
    }
  }

  return (
    <>
      <p className="m-0 mb-4 mt-8 text-[22px] font-normal leading-[28px] tracking-[-0.25px] text-black">{title}</p>
      <p className="m-0 text-center text-[14px] font-normal leading-[18px] tracking-[-0.25px] text-[#000000d9]">
        {desc}
      </p>
      <PrimaryNavLinkButton to="/" className="mx-[0px] my-[20px] h-[35px] w-[150px]">
        Go to dashboard
      </PrimaryNavLinkButton>
    </>
  );
};

interface DebugLogsType {
  error: unknown;
}

const DebugLogs = ({ error }: DebugLogsType) => {
  if (process.env.NODE_ENV !== 'development') return null;

  // Status handler
  if (isRouteErrorResponse(error)) {
    return (
      <pre style={{ width: '100%' }}>
        <p className="m-0 text-[14px] font-[500] leading-[18px] tracking-[-0.25px] text-[#000000d9]">Debug Logs:</p>
        <code style={{ margin: '15px 0' }}>{`Backend responded with: ${error.status} ${error.statusText}`}</code>
        <code style={{ margin: '15px 0' }}>{JSON.stringify(error, null, 2)}</code>
      </pre>
    );
  }

  // Unexpected error handler
  return (
    <pre style={{ width: '100%' }}>
      <p className="m-0 text-[14px] font-[500] leading-[18px] tracking-[-0.25px] text-[#000000d9]">Debug Logs:</p>
      <code style={{ margin: '15px 0' }}>{getErrorMessage(error)}</code>
      <code style={{ margin: '15px 0' }}>{getErrorStack(error)}</code>
    </pre>
  );
};

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;

  if (typeof error === 'string') return error;

  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  console.error('Unable to get error message for error', error);

  return 'Unknown Error';
}

function getErrorStack(error: unknown) {
  if (error instanceof Error) return error.stack;

  if (typeof error === 'string') return error;

  if (error && typeof error === 'object' && 'stack' in error && typeof error.stack === 'string') {
    return error.stack;
  }

  console.error('Unable to get error stack for error', error);

  return 'Unknown Error';
}
